import React, { useState } from 'react';
import { Share2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.jsx';
import ConnectMetaButton from '../components/social/ConnectMetaButton';
import MetaConnectionStatus from '../components/social/MetaConnectionStatus';
import ConnectLinkedInButton from '../components/social/ConnectLinkedInButton';
import LinkedInConnectionStatus from '../components/social/LinkedInConnectionStatus';
import ConnectTwitterButton from '../components/social/ConnectTwitterButton';
import TwitterConnectionStatus from '../components/social/TwitterConnectionStatus';
import ConnectTikTokButton from '../components/social/ConnectTikTokButton';
import TikTokConnectionStatus from '../components/social/TikTokConnectionStatus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Social Media Icons (SVG) - lucide-react doesn't have these
const InstagramIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const LinkedinIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const TwitterIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const TikTokIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const SocialAccounts = () => {
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div data-testid="social-accounts-page" className="space-y-8">
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-white mb-2">Réseaux Sociaux</h1>
        <p className="text-base text-zinc-400">Connectez et gérez vos comptes de réseaux sociaux</p>
      </div>

      {/* Tabs for different platforms */}
      <Tabs defaultValue="meta" className="space-y-6">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger 
            value="meta" 
            className="data-[state=active]:bg-indigo-600"
            data-testid="tab-meta"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Meta (Facebook & Instagram)
          </TabsTrigger>
          <TabsTrigger 
            value="linkedin" 
            className="data-[state=active]:bg-indigo-600"
            data-testid="tab-linkedin"
          >
            <LinkedinIcon className="w-4 h-4 mr-2" />
            LinkedIn
          </TabsTrigger>
          <TabsTrigger 
            value="twitter" 
            className="data-[state=active]:bg-indigo-600"
            data-testid="tab-twitter"
          >
            <TwitterIcon className="w-4 h-4 mr-2" />
            X/Twitter
          </TabsTrigger>
          <TabsTrigger 
            value="tiktok" 
            className="data-[state=active]:bg-indigo-600"
            data-testid="tab-tiktok"
          >
            <TikTokIcon className="w-4 h-4 mr-2" />
            TikTok
          </TabsTrigger>
        </TabsList>

        {/* Meta Tab */}
        <TabsContent value="meta" className="space-y-6">
          {/* Connect New Accounts Card */}
          <Card className="bg-zinc-900 border border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Share2 className="w-5 h-5 text-blue-500" />
                Connecter un Compte Meta
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Connectez vos Pages Facebook et comptes Instagram Business via OAuth
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Facebook Connect */}
                <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-600/20 rounded-lg">
                      <Share2 className="w-6 h-6 text-blue-500" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Facebook</h3>
                      <p className="text-sm text-zinc-400">Pages professionnelles</p>
                    </div>
                  </div>
                  <ConnectMetaButton 
                    provider="facebook" 
                    variant="default"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  />
                </div>

                {/* Instagram Connect */}
                <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-pink-600/20 rounded-lg">
                      <InstagramIcon className="w-6 h-6 text-pink-500" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Instagram</h3>
                      <p className="text-sm text-zinc-400">Comptes Business</p>
                    </div>
                  </div>
                  <ConnectMetaButton 
                    provider="instagram" 
                    variant="default"
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  />
                </div>
              </div>

              <div className="p-4 bg-blue-600/10 border border-blue-500/20 rounded-lg">
                <p className="text-sm text-blue-300">
                  💡 <strong>Astuce :</strong> Vous aurez besoin d'une Page Facebook pour connecter Instagram Business. 
                  Les comptes personnels Instagram ne sont pas supportés.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Connected Accounts Card */}
          <Card className="bg-zinc-900 border border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Comptes Connectés</CardTitle>
              <CardDescription className="text-zinc-400">
                Gérez vos comptes Meta connectés (OAuth 2.0)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <MetaConnectionStatus 
                key={refreshKey}
                onRefresh={handleRefresh} 
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* LinkedIn Tab */}
        <TabsContent value="linkedin" className="space-y-6">
          {/* Connect LinkedIn Card */}
          <Card className="bg-zinc-900 border border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <LinkedinIcon className="w-5 h-5 text-blue-500" />
                Connecter LinkedIn
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Connectez vos profils personnels et pages d'entreprise LinkedIn via OAuth 2.0
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-600/20 rounded-lg">
                    <LinkedinIcon className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">LinkedIn</h3>
                    <p className="text-sm text-zinc-400">Profils & Pages Entreprise</p>
                  </div>
                </div>
                <ConnectLinkedInButton 
                  variant="default"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                />
              </div>

              <div className="p-4 bg-blue-600/10 border border-blue-500/20 rounded-lg">
                <p className="text-sm text-blue-300">
                  💡 <strong>Astuce :</strong> Connectez votre profil personnel pour publier sur votre feed, 
                  ou connectez une page d'entreprise pour publier au nom de votre organisation.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Connected LinkedIn Accounts */}
          <Card className="bg-zinc-900 border border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Comptes LinkedIn Connectés</CardTitle>
              <CardDescription className="text-zinc-400">
                Gérez vos comptes LinkedIn connectés (OAuth 2.0)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LinkedInConnectionStatus 
                key={refreshKey}
                onRefresh={handleRefresh} 
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Twitter Tab (Coming Soon) */}
        <TabsContent value="twitter" className="space-y-6">
          {/* Connect Twitter Card */}
          <Card className="bg-zinc-900 border border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TwitterIcon className="w-5 h-5 text-blue-400" />
                Connecter Twitter/X
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Connectez votre compte Twitter/X via OAuth 2.0 avec PKCE
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-600/20 rounded-lg">
                    <TwitterIcon className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Twitter/X</h3>
                    <p className="text-sm text-zinc-400">Compte personnel</p>
                  </div>
                </div>
                <ConnectTwitterButton 
                  variant="default"
                  className="w-full bg-blue-500 hover:bg-blue-600"
                />
              </div>

              <div className="p-4 bg-blue-600/10 border border-blue-500/20 rounded-lg">
                <p className="text-sm text-blue-300">
                  💡 <strong>Astuce :</strong> Publiez des tweets, images et vidéos directement depuis NEXORA AI.
                  Planifiez vos publications et suivez vos performances.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Connected Twitter Accounts */}
          <Card className="bg-zinc-900 border border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Comptes Twitter Connectés</CardTitle>
              <CardDescription className="text-zinc-400">
                Gérez vos comptes Twitter connectés (OAuth 2.0 + PKCE)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TwitterConnectionStatus 
                key={refreshKey}
                onRefresh={handleRefresh} 
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* TikTok Tab */}
        <TabsContent value="tiktok" className="space-y-6">
          {/* Connect TikTok Card */}
          <Card className="bg-zinc-900 border border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TikTokIcon className="w-5 h-5 text-pink-500" />
                Connecter TikTok
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Connectez votre compte TikTok Business via OAuth 2.0
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-pink-600/20 rounded-lg">
                    <TikTokIcon className="w-6 h-6 text-pink-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">TikTok Business</h3>
                    <p className="text-sm text-zinc-400">Compte professionnel</p>
                  </div>
                </div>
                <ConnectTikTokButton 
                  variant="default"
                  className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
                />
              </div>

              <div className="p-4 bg-pink-600/10 border border-pink-500/20 rounded-lg">
                <p className="text-sm text-pink-300">
                  💡 <strong>Astuce :</strong> Publiez vos vidéos TikTok directement depuis NEXORA AI.
                  Planifiez et gérez votre contenu en un seul endroit.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Connected TikTok Accounts */}
          <Card className="bg-zinc-900 border border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Comptes TikTok Connectés</CardTitle>
              <CardDescription className="text-zinc-400">
                Gérez vos comptes TikTok Business (OAuth 2.0)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TikTokConnectionStatus 
                key={refreshKey}
                onRefresh={handleRefresh} 
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SocialAccounts;